'use client';

import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useState, useEffect } from 'react';
import { Search, X, Filter, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { getCategoryLabel, CONDITION_OPTIONS } from '@/lib/constants/products';

export default function Controls({ categories, brands, minPrice, maxPrice }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [priceRange, setPriceRange] = useState({
    min: searchParams.get('minPrice') || '',
    max: searchParams.get('maxPrice') || '',
  });

  const createQueryString = useCallback(
    (updates) => {
      const params = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([name, value]) => {
        if (value && value !== '') {
          params.set(name, value);
        } else {
          params.delete(name);
        }
      });
      return params.toString();
    },
    [searchParams]
  );

  const updateFilters = (updates) => {
    router.push(pathname + '?' + createQueryString(updates));
  };

  const handleSelectChange = (name, value) => {
    const currentParams = {
      sortBy: searchParams.get('sortBy') || '',
      category: searchParams.get('category') || '',
      brand: searchParams.get('brand') || '',
      condition: searchParams.get('condition') || '',
      search: searchParams.get('search') || '',
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
    };
    currentParams[name] = value;
    updateFilters(currentParams);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const currentParams = {
      sortBy: searchParams.get('sortBy') || '',
      category: searchParams.get('category') || '',
      brand: searchParams.get('brand') || '',
      condition: searchParams.get('condition') || '',
      search: searchQuery,
      minPrice: searchParams.get('minPrice') || '',
      maxPrice: searchParams.get('maxPrice') || '',
    };
    updateFilters(currentParams);
  };

  const handlePriceRange = () => {
    const currentParams = {
      sortBy: searchParams.get('sortBy') || '',
      category: searchParams.get('category') || '',
      brand: searchParams.get('brand') || '',
      condition: searchParams.get('condition') || '',
      search: searchParams.get('search') || '',
      minPrice: priceRange.min,
      maxPrice: priceRange.max,
    };
    updateFilters(currentParams);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setPriceRange({ min: '', max: '' });
    router.push(pathname);
  };

  const activeFiltersCount = [
    searchParams.get('category'),
    searchParams.get('brand'),
    searchParams.get('condition'),
    searchParams.get('minPrice'),
    searchParams.get('maxPrice'),
    searchParams.get('search'),
  ].filter(Boolean).length;

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">

          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit..."
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] text-sm bg-white"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    handleSelectChange('search', '');
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </form>


          <div className="w-full sm:w-auto sm:min-w-[160px]">
            <select
              id="sortBy"
              onChange={(e) => handleSelectChange('sortBy', e.target.value)}
              value={searchParams.get('sortBy') || ''}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] bg-white"
            >
              <option value="">Trier par</option>
              <option value="price-asc">Prix ↑</option>
              <option value="price-desc">Prix ↓</option>
              <option value="name-asc">Nom A-Z</option>
              <option value="name-desc">Nom Z-A</option>
            </select>
          </div>


          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm transition-colors w-full sm:w-auto"
          >
            <SlidersHorizontal size={16} />
            <span>Filtres</span>
            {activeFiltersCount > 0 && (
              <span className="bg-[#23994F] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>


        <div className="flex gap-2 overflow-x-auto scrollbar-hide mt-2 pb-1">
          <button
            onClick={() => handleSelectChange('category', '')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
              !searchParams.get('category')
                ? 'bg-[#23994F] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tous
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => handleSelectChange('category', cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                searchParams.get('category') === cat
                  ? 'bg-[#23994F] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>


        {isFiltersOpen && (
          <div className="mt-3 bg-gray-50 rounded-lg border border-gray-200 p-4 animate-slideDown">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">Filtres avancés</h3>
              <button onClick={() => setIsFiltersOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">

              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Marque</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleSelectChange('brand', '')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      !searchParams.get('brand')
                        ? 'bg-[#23994F] text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-[#23994F]'
                    }`}
                  >
                    Toutes
                  </button>
                  {brands.map(brand => (
                    <button
                      key={brand}
                      onClick={() => handleSelectChange('brand', brand)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        searchParams.get('brand') === brand
                          ? 'bg-[#23994F] text-white'
                          : 'bg-white text-gray-700 border border-gray-200 hover:border-[#23994F]'
                      }`}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              </div>


              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">État</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleSelectChange('condition', '')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      !searchParams.get('condition')
                        ? 'bg-[#23994F] text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:border-[#23994F]'
                    }`}
                  >
                    Tous
                  </button>
                  {CONDITION_OPTIONS.map(cond => (
                    <button
                      key={cond.value}
                      onClick={() => handleSelectChange('condition', cond.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        searchParams.get('condition') === cond.value
                          ? 'bg-[#23994F] text-white'
                          : 'bg-white text-gray-700 border border-gray-200 hover:border-[#23994F]'
                      }`}
                    >
                      {cond.label}
                    </button>
                  ))}
                </div>
              </div>


              <div>
                <label className="text-xs font-semibold text-gray-700 mb-2 block">Prix ({minPrice}€ - {maxPrice}€)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] bg-white"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#23994F] focus:border-[#23994F] bg-white"
                  />
                  <button
                    onClick={handlePriceRange}
                    className="px-4 py-2 bg-[#23994F] text-white rounded-lg text-xs font-semibold hover:bg-[#1a7840] transition-all"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>


            {activeFiltersCount > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                >
                  <X size={14} />
                  Réinitialiser ({activeFiltersCount})
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
